import { delay, put, takeLatest, call, select } from "redux-saga/effects";
import { updateAlerts } from "../Reducers/alertsReducer";
import {
  GetAlertsAction,
  CreateAlertAction,
  UpdateAlertAction,
  DeleteAlertAction,
  ToggleAlertAction,
} from "../Actions/action";
import * as alertsService from "../../services/alertsService";
import { apiResponse, onError } from "../../helper/helper";
import { toast } from "react-toastify";
import { toastConfiguration } from "../../Constant/Data";

function* getAlertsSaga(action) {
  try {
    yield put(
      updateAlerts({
        alertsLoading: true,
      })
    );
    
    let { page, search, location,isAdminLoggedIn } = action?.payload || {};
    
    // Check if we already have location in global state
    const { Alerts } = yield select();
    if (!location && Alerts?.userLocation) {
      location = `${Alerts.userLocation.city},${Alerts.userLocation.country}`;
    }
    
    const { ok, data, originalError, status } = yield call(
      alertsService.getAlerts,
      { page, search, location,isAdminLoggedIn }
    );

    if (ok) {
      const { data: response, meta } = data || {};
      
      // If location was fetched from API, save it to global state
      if (!Alerts?.userLocation && location) {
        // Extract city and country from location string
        const [city, country] = location.split(',');
        yield put(
          updateAlerts({
            userLocation: {
              city: city?.trim(),
              country: country?.trim(),
              name: location
            }
          })
        );
      }
      
      yield put(
        updateAlerts({
          alertsList: response?.response || [],
          // currentPage: meta?.current_page || 1,
          // lastPage: meta?.last_page || 0,
          // totalAlertsCount: meta?.total || 0,
          // perPageCount: meta?.per_page || 0,
        })
      );
    } else {
      if (status === 404) {
        yield put(
          updateAlerts({
            alertsList: [],
          })
        );
        return;
      }
      const errorMsg = Boolean(typeof data?.message === "string");
      const message = data?.message || originalError?.message;
      if (errorMsg) toast.error(message,toastConfiguration);
      else toast.error(apiResponse(message),toastConfiguration);
    }
  } catch (error) {
    console.log("error-----------", error);
  } finally {
    yield put(
      updateAlerts({
        alertsLoading: false,
      })
    );
  }
}

function* createAlertSaga(action) {
  try {
    yield put(
      updateAlerts({
        createAlertLoading: true,
      })
    );
    
    const { ok, data, originalError } = yield call(
      alertsService.createAlert,
      action?.payload
    );
    
    if (ok) {
      const { data: newAlert } = data || {};
      const { Alerts } = yield select();
      yield put(
        updateAlerts({
          alertsList: [newAlert, ...Alerts?.alertsList],
        })
      );
      yield toast.success("Alert created successfully", toastConfiguration);
    } else {
      const errorMsg = Boolean(typeof data?.message === "string");
      const message = data?.message || originalError?.message;
      if (errorMsg) toast.error(message);
      else toast.error(apiResponse(message));
    }
  } catch (error) {
    console.log("error-----------", error);
  } finally {
    yield put(
      updateAlerts({
        createAlertLoading: false,
      })
    );
  }
}

function* updateAlertSaga(action) {
  try {
    yield put(
      updateAlerts({
        updateAlertLoading: true,
      })
    );
    
    const { id, ...alertData } = action?.payload || {};
    const { ok, data, originalError } = yield call(
      alertsService.updateAlert,
      { id, ...alertData }
    );
    
    if (ok) {
      const { data: updatedAlert } = data || {};
      const { Alerts } = yield select();
      const updatedList = Alerts?.alertsList?.map((alert) =>
        alert.id === id ? updatedAlert : alert
      );
      yield put(
        updateAlerts({
          alertsList: updatedList,
        })
      );
      yield toast.success("Alert updated successfully", toastConfiguration);
    } else {
      const errorMsg = Boolean(typeof data?.message === "string");
      const message = data?.message || originalError?.message;
      if (errorMsg) toast.error(message);
      else toast.error(apiResponse(message));
    }
  } catch (error) {
    console.log("error-----------", error);
  } finally {
    yield put(
      updateAlerts({
        updateAlertLoading: false,
      })
    );
  }
}

function* deleteAlertSaga(action) {
  try {
    yield put(
      updateAlerts({
        deleteAlertLoading: true,
      })
    );
    
    const { id } = action?.payload || {};
    const { ok, data, originalError } = yield call(
      alertsService.deleteAlert,
      { id }
    );
    
    if (ok) {
      const { Alerts } = yield select();
      const updatedList = Alerts?.alertsList?.filter((alert) => alert.id !== id);
      yield put(
        updateAlerts({
          alertsList: updatedList,
        })
      );
      yield toast.success("Alert deleted successfully", toastConfiguration);
    } else {
      const errorMsg = Boolean(typeof data?.message === "string");
      const message = data?.message || originalError?.message;
      if (errorMsg) toast.error(message);
      else toast.error(apiResponse(message));
    }
  } catch (error) {
    console.log("error-----------", error);
  } finally {
    yield put(
      updateAlerts({
        deleteAlertLoading: false,
      })
    );
  }
}

function* toggleAlertSaga(action) {
  try {
    const { id, is_active } = action?.payload || {};
    const { ok, data, originalError } = yield call(
      alertsService.toggleAlert,
      { id, is_active }
    );
    
    if (ok) {
      const { data: updatedAlert } = data || {};
      const { Alerts } = yield select();
      const updatedList = Alerts?.alertsList?.map((alert) =>
        alert.id === id ? updatedAlert : alert
      );
      yield put(
        updateAlerts({
          alertsList: updatedList,
        })
      );
      yield toast.success(
        `Alert ${is_active ? "activated" : "deactivated"} successfully`,
        toastConfiguration
      );
    } else {
      const errorMsg = Boolean(typeof data?.message === "string");
      const message = data?.message || originalError?.message;
      if (errorMsg) toast.error(message);
      else toast.error(apiResponse(message));
    }
  } catch (error) {
    console.log("error-----------", error);
  }
}

function* alertsSaga() {
  yield takeLatest(GetAlertsAction, getAlertsSaga);
  yield takeLatest(CreateAlertAction, createAlertSaga);
  yield takeLatest(UpdateAlertAction, updateAlertSaga);
  yield takeLatest(DeleteAlertAction, deleteAlertSaga);
  yield takeLatest(ToggleAlertAction, toggleAlertSaga);
}

export default alertsSaga;
